import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AddDomainInput, DomainAttributes } from '@loyalty/shared-common/gamification-api-client';
import { Alert } from '@mui/material';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { Loader } from '../shared/layout/Loader';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';

export interface SubdomainsContextInterface {
  subdomains: DomainAttributes[];
  addSubdomain: (accountId: string, domain: AddDomainInput) => void;
  getSubdomains: () => void;
  deleteSubdomain: (domain: DomainAttributes) => void;
  setupSubdomain: (domain: DomainAttributes) => Promise<DomainAttributes>;
  isLoading: IsLoading;
}

export const SubdomainsContext = createContext<SubdomainsContextInterface>({} as any);

export function useDomains() {
  return useContext(SubdomainsContext);
}

export function SubdomainsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { currentAccount } = useAccounts();
  const { gamificationApi, managerApi } = useApi();
  const [error, setError] = useState<any>();
  const [subdomains, setSubdomains] = useState<DomainAttributes[]>([]);

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const addSubdomain = useCallback(
    (accountId: string, domain: AddDomainInput) => {
      addLoader('addSubdomain');
      gamificationApi
        .addSubdomain(accountId, {
          programId: domain.programId,
          domain: domain.domain,
          developmentMode: domain.developmentMode,
        })
        .then(newSubdomain => {
          setSubdomains(subdomains => [...subdomains, newSubdomain]);
          notify(t('shared.actions.saved'), 'success');
        })
        .catch(error => notify(t('domains.errors.failed-save', { error }), 'error'))
        .finally(() => removeLoader('addSubdomain'));
    },
    [gamificationApi, notify, t, addLoader, removeLoader],
  );

  const getSubdomains = useCallback(() => {
    if (currentAccount) {
      addLoader('getSubdomains');
      gamificationApi
        .getSubdomains({ accountId: currentAccount.id })
        .then(subdomains => {
          setSubdomains(subdomains);
        })
        .catch(setError)
        .finally(() => removeLoader('getSubdomains'));
    }
  }, [gamificationApi, currentAccount, addLoader, removeLoader]);

  const deleteSubdomain = useCallback(
    (domain: DomainAttributes) => {
      addLoader('deleteSubdomain');
      managerApi
        .deleteSubdomain(domain)
        .then(() => {
          setSubdomains(subdomains =>
            subdomains.map(subdomain =>
              subdomain.id === domain.id
                ? { ...subdomain, deletedAt: new Date().toJSON() }
                : subdomain,
            ),
          );
        })
        .catch(error => {
          notify(t('subdomain.sidebar.archive.error', { error }), 'error');
          getSubdomains();
        })
        .finally(() => removeLoader('deleteSubdomain'));
    },
    [managerApi, t, notify, addLoader, removeLoader, getSubdomains],
  );

  const setupSubdomain = useCallback(
    (domain: DomainAttributes) => {
      addLoader('setupSubdomain');
      return managerApi
        .setupSubdomain(domain)
        .then(updatedSubdomain => {
          setSubdomains(domains => domains.map(p => (p.id === domain.id ? updatedSubdomain : p)));
          return updatedSubdomain;
        })
        .finally(() => removeLoader('setupSubdomain'));
    },
    [managerApi, addLoader, removeLoader],
  );

  useEffect(() => {
    getSubdomains();
  }, [getSubdomains]);

  const context = {
    subdomains,
    addSubdomain,
    getSubdomains,
    deleteSubdomain,
    setupSubdomain,
    isLoading,
  };

  return isLoading('getSubdomains') ? (
    <Loader />
  ) : error ? (
    <Alert severity="error">{error.toString()}</Alert>
  ) : (
    <SubdomainsContext.Provider value={context}>{children}</SubdomainsContext.Provider>
  );
}
